import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';

/**
 * CustomSelect — a fully styled dropdown that matches the height and padding
 * of standard <input> elements. Supports keyboard navigation.
 * Uses a React portal so the list renders at document.body level,
 * escaping all stacking contexts (including Leaflet map panes).
 *
 * Props:
 *   value       — current selected value
 *   onChange    — fn(value) called on selection
 *   options     — array of { value, label } OR array of strings
 *   placeholder — shown when value is empty
 *   className   — extra class on the trigger (e.g. 'invalid')
 *   disabled    — boolean
 */
function CustomSelect({ value, onChange, options, placeholder = 'Select...', className = '', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [rect, setRect] = useState(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  // Normalize options to { value, label }
  const normalized = options.map(o =>
    typeof o === 'string' ? { value: o, label: o } : o
  );

  const selectedLabel = normalized.find(o => String(o.value) === String(value))?.label ?? placeholder;

  const close = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  const openList = useCallback(() => {
    if (!containerRef.current) return;
    setRect(containerRef.current.getBoundingClientRect());
    setOpen(true);
  }, []);

  // Close on outside click (must check both container and portal list)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const inContainer = containerRef.current?.contains(e.target);
      const inList = listRef.current?.contains(e.target);
      if (!inContainer && !inList) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close]);

  // Scroll focused item into view
  useEffect(() => {
    if (!open || focusedIndex < 0) return;
    const item = listRef.current?.children[focusedIndex];
    item?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex, open]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (containerRef.current) setRect(containerRef.current.getBoundingClientRect());
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const handleKeyDown = (e) => {
    if (disabled) return;
    switch (e.key) {
      case ' ':
        e.preventDefault();
        if (!open) { openList(); setFocusedIndex(normalized.findIndex(o => String(o.value) === String(value))); }
        else if (focusedIndex >= 0) { onChange(normalized[focusedIndex].value); close(); }
        break;
      case 'Enter':
        if (open) {
          e.preventDefault();
          if (focusedIndex >= 0) { onChange(normalized[focusedIndex].value); close(); }
        }
        // When closed, let Enter bubble up to submit the form
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) { openList(); setFocusedIndex(0); }
        else setFocusedIndex(i => Math.min(i + 1, normalized.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Escape':
        close();
        break;
      default:
        break;
    }
  };

  const handleSelect = (val) => {
    onChange(val);
    close();
  };

  const listStyle = rect ? {
    position: 'fixed',
    top: rect.bottom + 4,
    left: rect.left,
    width: rect.width,
    zIndex: 99999,
  } : {};

  return (
    <div
      ref={containerRef}
      className={`custom-select-container${open ? ' open' : ''}${disabled ? ' disabled' : ''}`}
      style={{ position: 'relative', userSelect: 'none' }}
    >
      <div
        className={`custom-select-trigger form-input ${className}`}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        tabIndex={disabled ? -1 : 0}
        onClick={() => { if (!disabled) { open ? close() : openList(); } }}
        onKeyDown={handleKeyDown}
      >
        <span className="custom-select-value">{selectedLabel}</span>
        <span className="custom-select-arrow" aria-hidden="true">
          {open ? '▲' : '▼'}
        </span>
      </div>

      {open && rect && createPortal(
        <ul
          ref={listRef}
          id={listId}
          className="custom-select-list"
          role="listbox"
          style={listStyle}
        >
          {normalized.map((opt, i) => (
            <li
              key={opt.value}
              className={`custom-select-option${String(opt.value) === String(value) ? ' selected' : ''}${i === focusedIndex ? ' focused' : ''}`}
              role="option"
              aria-selected={String(opt.value) === String(value)}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(opt.value); }}
              onMouseEnter={() => setFocusedIndex(i)}
            >
              {opt.sublabel || opt.label}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
}

export default CustomSelect;
